import { SharedIcon, SharedIconProps } from 'components/SharedIcon/SharedIcon';
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import RoleTag from './RoleTag';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { MaterialSymbols } from 'material-design-icons-literal-types';
import AIReviewButton from 'components/DisasterGPT/AIReviewButton';
import type { AIReviewActionKey } from 'components/DisasterGPT/reviewActions';

type HeaderLeftProps = {
  iconName: MaterialSymbols;
  iconColor: SharedIconProps['color'];
  title: string;
  subtitle: string;
  titleInfoTooltip?: string;
};

const HeaderLeft: React.FC<HeaderLeftProps> = ({
  iconName,
  iconColor,
  title,
  subtitle,
  titleInfoTooltip,
}) => {
  return (
    <div className="d-flex flex-column align-items-start">
      <h3 className="d-flex align-items-center mt-0 mb-0 flex-wrap gap-1">
        <SharedIcon iconName={iconName} classes="me-2" color={iconColor} />
        <span>{title}</span>
        {titleInfoTooltip ? (
          <OverlayTrigger
            placement="top"
            overlay={(props) => (
              <Tooltip id="ics-widget-header-info-tooltip" {...props}>
                {titleInfoTooltip}
              </Tooltip>
            )}
          >
            <span
              className="d-inline-flex align-items-center text-weak"
              role="button"
              tabIndex={0}
              aria-label={titleInfoTooltip}
            >
              <SharedIcon iconName="info" size="sm" color="primary" />
            </span>
          </OverlayTrigger>
        ) : null}
      </h3>
      <div className="text-weak ps-4">{subtitle}</div>
    </div>
  );
};

type HeaderRightProps = {
  icsRoles: string[];
  handleExport?: () => void;
  exportText?: string;
  disabledExport?: boolean;
  /**
   * Widget key from AI_REVIEW_SCENARIOS — renders the standardized "Review X"
   * AI button in the header cluster (see DEFINITION_OF_DONE.md).
   */
  reviewActionKey?: AIReviewActionKey;
};

const HeaderRight: React.FC<HeaderRightProps> = ({
  icsRoles,
  handleExport,
  exportText,
  disabledExport,
  reviewActionKey,
}) => {
  return (
    <div className="d-flex align-items-center gap-2">
      {reviewActionKey && <AIReviewButton actionKey={reviewActionKey} />}
      <RoleTag icsRoles={icsRoles} />
      {handleExport && (
        <StylishNewButton
          className="button button--secondary gap-1"
          onClick={(e) => {
            e.stopPropagation();
            handleExport();
          }}
          disabled={disabledExport}
        >
          <SharedIcon iconName="visibility" size="sm" /> {exportText}
        </StylishNewButton>
      )}
    </div>
  );
};

export const ICSWidget = {
  HeaderLeft,
  HeaderRight,
};

export default ICSWidget;
